import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useERSContext } from '@/context';
import { ChatWindow } from './ChatWindow/ChatWindow';
import { ChatSidebar } from './ChatSidebar/ChatSidebar';
import { constants } from '@/lib/constants';
import { ChatSkeleton } from '@/components/shared/loader';

export const Chat = () => {
  const {
    unreadInfo,
    userSpeaking,
    groups,
    users,
    pinnedIds,
    incomingVideoStreams,
    selectedChatId,
    setSelectedChatId,
    chatList,
    setChatList,
    selectedChat,
    setSelectedChat,
    chatMembers,
    setChatMembers,
    pinnedChats,
    setPinnedChats,
    mutedChats,
    setMutedChats,
    handleChatSelect,
    togglePinChat,
    toggleMuteChat,
    isChatMuted,
    isChatPinned,
  } = useERSContext();

  const [isLoading, setIsLoading] = useState(true);

  const groupsRef = useRef(groups);
  const usersRef = useRef(users);
  const initializedRef = useRef(false);
  const prevGroupIdsRef = useRef([]);

  useEffect(() => {
    groupsRef.current = groups;
    usersRef.current = users;

    if ((groups.length > 0 || users.length > 0) && !initializedRef.current) {
      initializedRef.current = true;
      getGroupList();
      loadPinnedChats();
      loadMutedChats();
    }

    const currentGroupIds = groups?.map(g => g.id).sort();
    const prevGroupIds = prevGroupIdsRef?.current?.sort();

    const newGroupAdded =
      currentGroupIds?.length > prevGroupIds?.length || currentGroupIds?.some(id => !prevGroupIds?.includes(id));

    if (newGroupAdded && initializedRef?.current) {
      getGroupList();
    }
    prevGroupIdsRef.current = currentGroupIds;
  }, [groups, users]);

  useEffect(() => {
    if (chatList.length > 0) {
      //delay a little bit, so it will avoid the racing condition in the very early start when app start
      setTimeout(() => {
        setChatList(prevChatList => {
          return prevChatList.map(chat => ({
            ...chat,
            unreadCount: unreadInfo[chat.id]?.count || 0,
            lastMessageTime: unreadInfo[chat.id]?.timestamp || null,
          }));
        });
      }, 300);
    }
  }, [unreadInfo]);

  useEffect(() => {
    if (pinnedIds && pinnedIds.length > 0) {
      setPinnedChats(new Set(pinnedIds));
    }
  }, [pinnedIds]);

  const getGroupList = useCallback(async () => {
    try {
      setIsLoading(true);
      const currentGroups = groupsRef.current;
      const currentUsers = usersRef.current;

      const response = [...currentGroups, ...currentUsers];

      const initialChatList = response.map(chat => ({
        ...chat,
        unreadCount: 0,
        lastMessageTime: null,
      }));

      setChatList(initialChatList);
    } catch (error) {
      console.error('Error in getGroupList:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadPinnedChats = () => {
    const savedPinnedChats = sessionStorage.getItem('pinnedChats');
    if (savedPinnedChats) {
      setPinnedChats(new Set(JSON.parse(savedPinnedChats)));
    }
  };

  const loadMutedChats = () => {
    const savedMutedChats = sessionStorage.getItem('mutedChats');
    if (savedMutedChats) {
      setMutedChats(new Set(JSON.parse(savedMutedChats)));
    }
  };

  const sortedChatList = React.useMemo(() => {
    return [...chatList].sort((a, b) => {
      const aPinned = isChatPinned(a.id);
      const bPinned = isChatPinned(b.id);

      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;

      const aTime = unreadInfo[a.id]?.timestamp || 0;
      const bTime = unreadInfo[b.id]?.timestamp || 0;
      return bTime - aTime;
    });
  }, [chatList, isChatPinned]);

  const handleSelectedChat = () => {
    setSelectedChat(null);
    setSelectedChatId('');
  };

  if (isLoading) {
    return <ChatSkeleton />;
  }

  return (
    <div className="p-3 bg flex w-full h-full">
      <div className="bg flex flex-row border border-border rounded-lg w-full min-w-[37.75rem] h-full overflow-hidden">
        <div className="flex w-1/3 border-r border-border overflow-hidden">
          <div className="flex flex-col w-full h-full overflow-hidden">
            <div className="flex-1 overflow-auto">
              <ChatSidebar
                chatList={sortedChatList}
                onChatSelect={handleChatSelect}
                selectedChat={selectedChat}
                onTogglePin={togglePinChat}
                onToggleMute={toggleMuteChat}
                pinnedChats={pinnedChats || new Set()}
                mutedChats={mutedChats || new Set()}
                userSpeaking={userSpeaking}
                unreadInfo={unreadInfo}
                isChatMuted={isChatMuted}
                incomingVideoStream={incomingVideoStreams}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <ChatWindow
            setSelectedChat={handleSelectedChat}
            selectedChat={selectedChat}
            chatMembers={selectedChat ? chatMembers[selectedChat.id] : []}
            onTogglePin={togglePinChat}
            onToggleMute={toggleMuteChat}
            isChatPinned={isChatPinned}
            isChatMuted={isChatMuted}
          />
        </div>
      </div>
    </div>
  );
};
